// Bande delle fasi nell'header del planning.
//
// Ogni fase occupa una banda CONTIGUA di colonne: comincia al primo turno in cui la fase compare
// e finisce dove comincia la successiva. La somma dei colSpan e' cosi' per costruzione uguale a
// maxTurno, e la riga delle fasi non puo' sfasarsi rispetto al corpo della tabella.
//
// Contare invece i turni distinti per fase rompe la tabella appena due fasi condividono un turno:
// la riga delle fasi diventa piu' larga del corpo e, con table-layout:fixed, e' lei a dettare il
// numero di colonne. Sul torneo 98 la somma faceva 28 contro 15 turni, e l'header sbordava a
// destra mentre le righe si fermavano a Turno 15.

/**
 * @param {Array<{fase?: number, turno: number}>} matches
 * @param {number} maxTurno
 * @param {Map<number, number>|Object} [confini] fase -> primo turno spostato a mano. Chi non
 *   compare usa il primo turno in cui la fase ha una partita.
 * @returns {Array<{fase: number, nome: ?string, turni: number[], startTurno: number, endTurno: number, count: number}>}
 */
export const raggruppaTurniPerFase = (matches, maxTurno, confini) => {
  if (!maxTurno || maxTurno < 1) return [];

  const turniPerFase = new Map();
  const nomiFase = new Map();
  matches.forEach((match) => {
    const fase = match.fase ?? 0;
    if (!turniPerFase.has(fase)) turniPerFase.set(fase, new Set());
    turniPerFase.get(fase).add(match.turno);
    if (match.faseNome && !nomiFase.has(fase)) nomiFase.set(fase, match.faseNome);
  });

  // Il confine spostato a mano vince sul primo turno in cui la fase compare: basta una partita
  // rimasta indietro perche' quello faccia cominciare la banda troppo presto.
  const confine = (fase) => {
    const v = confini instanceof Map ? confini.get(fase) : confini?.[fase];
    return Number.isInteger(v) && v >= 1 ? v : null;
  };

  // Le fasi si susseguono nell'ordine in cui entrano in calendario.
  const ordinate = Array.from(turniPerFase.entries())
    .map(([fase, turni]) => ({ fase, primo: confine(fase) ?? Math.min(...turni), turni }))
    .sort((a, b) => a.primo - b.primo || a.fase - b.fase);

  const fasi = [];
  let cursore = 1;
  ordinate.forEach((f, i) => {
    if (cursore > maxTurno) return; // niente colonne rimaste: fase senza banda
    // La banda successiva non puo' cominciare prima di lasciare una colonna a questa.
    const inizioProssima = i === ordinate.length - 1
      ? maxTurno + 1
      : Math.max(ordinate[i + 1].primo, cursore + 1);
    const fine = Math.min(inizioProssima - 1, maxTurno);
    fasi.push({
      fase: f.fase,
      nome: nomiFase.get(f.fase) ?? null,
      turni: Array.from(f.turni).sort((a, b) => a - b),
      startTurno: cursore,
      endTurno: fine,
      count: fine - cursore + 1,
    });
    cursore = fine + 1;
  });

  // Una coda di turni senza partite (capita inserendo turni in fondo) va all'ultima banda, cosi'
  // la somma dei colSpan resta esatta.
  if (cursore <= maxTurno && fasi.length > 0) {
    const ultima = fasi[fasi.length - 1];
    ultima.endTurno = maxTurno;
    ultima.count = maxTurno - ultima.startTurno + 1;
  }

  return fasi;
};

/**
 * Le partite che si giocano fuori dalla banda della loro fase. Violano l'invariante "una fase
 * occupa turni consecutivi e tutte le sue partite stanno li' dentro": non le nascondiamo, le
 * elenchiamo perche' si possano spostare.
 */
export const partiteFuoriFase = (matches, fasiRaggruppate) => {
  const bandaPerFase = new Map(fasiRaggruppate.map((f) => [f.fase, f]));
  return matches.filter((m) => {
    const banda = bandaPerFase.get(m.fase ?? 0);
    return banda && (m.turno < banda.startTurno || m.turno > banda.endTurno);
  });
};

/** Il titolo della banda: il nome dichiarato nella formula, o "Fase N". */
export const titoloFase = (faseInfo) =>
  faseInfo.nome || `Fase ${faseInfo.fase === 0 ? 'Regolare' : faseInfo.fase}`;

/**
 * Lo sfondo dell'header di un turno, per fase.
 *
 * Sei tinte profonde che si riciclano: la prima e' lo slate che la pagina usava per tutti i
 * turni, cosi' la fase a gironi resta com'era e sono le altre a staccarsi. Sono scure apposta -
 * il bianco sopra sta fra 7,4 e 9,5 di contrasto, e l'etichetta piccola, che viaggia all'85% di
 * opacita', resta sopra 5,9 - e distinte apposta: la coppia piu' vicina dista dE 25, che a
 * colpo d'occhio su un telefono al sole e' una differenza che si vede.
 */
const COLORI_FASE = [
  '#34495e', // slate, la fase a gironi
  '#145a52', // teal profondo
  '#5b2d8e', // viola profondo
  '#8c2f39', // rosso mattone
  '#7a4a12', // bronzo
  '#3f5d2a', // verde bosco
];

/** @param {?number} fase @returns {string} */
export const coloreFase = (fase) =>
  COLORI_FASE[(Number.isInteger(fase) && fase >= 0 ? fase : 0) % COLORI_FASE.length];
